/**
 * Mapeamento centralizado: status granular → status_category
 * Usado pelo StageCard ao mudar status — o status_category é derivado automaticamente.
 * O analista nunca precisa escolher a categoria manualmente.
 *
 * Categorias:
 * - not_started: nenhuma ação ainda
 * - internal_action: time interno está trabalhando
 * - external_wait: aguardando resposta de fora (USCIS, governo, etc.)
 * - action_required: precisa de atenção urgente
 * - completed: etapa finalizada com sucesso
 * - failed: etapa negada/falhou
 */

export const STATUS_CATEGORY_MAP: Record<string, string> = {
  // not_started
  pending: 'not_started',
  waiting: 'not_started',

  // internal_action (time está trabalhando)
  collecting: 'internal_action',
  preparing: 'internal_action',
  recruitment: 'internal_action',
  quiet_period: 'internal_action',
  clock_paused: 'internal_action',

  // external_wait (esperando resposta de fora)
  sent: 'external_wait',
  filed: 'external_wait',
  submitted: 'external_wait',
  scheduled: 'external_wait',
  clock_running: 'external_wait',
  swa_posted: 'external_wait',
  ads_published: 'external_wait',
  recruiting: 'external_wait',
  eligible: 'external_wait',
  card_produced: 'external_wait',
  card_mailed: 'external_wait',
  current: 'external_wait',

  // action_required (precisa de atenção urgente)
  rfi: 'internal_action',
  rfi_received: 'action_required',
  rfe_received: 'internal_action',
  noid_received: 'internal_action',
  rfe_responded: 'external_wait',
  noid_responded: 'external_wait',
  missed: 'action_required',

  // internal_action / external_wait adicionais
  in_progress: 'internal_action',
  partial: 'internal_action',
  requested: 'external_wait',
  waitlisted: 'external_wait',
  hearing_scheduled: 'external_wait',
  passport_submitted: 'external_wait',

  // completed
  completed: 'completed',
  approved: 'completed',
  certified: 'completed',
  receipt_received: 'external_wait',
  card_received: 'completed',
  received: 'completed',
  rfi_responded: 'completed',
  created: 'completed',
  bona_fide: 'completed',
  not_applicable: 'completed',
  visa_obtained: 'completed',
  entered: 'completed',
  issued: 'completed',
  decided: 'completed',
  paid: 'completed',
  confirmed: 'completed',
  order_obtained: 'completed',

  // failed
  denied: 'failed',
};

export type StatusCategory =
  | 'not_started'
  | 'internal_action'
  | 'external_wait'
  | 'action_required'
  | 'completed'
  | 'failed';

export function getStatusCategory(status: string): StatusCategory {
  return (STATUS_CATEGORY_MAP[status] as StatusCategory) || 'not_started';
}

/** Cores por categoria — para uso nos componentes de UI */
export const STATUS_CATEGORY_COLORS: Record<StatusCategory, {
  text: string;
  bg: string;
  border: string;
  label: string;
}> = {
  not_started: {
    text: 'text-muted-foreground',
    bg: 'bg-muted',
    border: 'border-border',
    label: 'Pendente',
  },
  internal_action: {
    text: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    border: 'border-amber-200 dark:border-amber-800',
    label: 'Ação Interna',
  },
  external_wait: {
    text: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    border: 'border-blue-200 dark:border-blue-800',
    label: 'Ação Externa',
  },
  action_required: {
    text: 'text-rose-600 dark:text-rose-400',
    bg: 'bg-rose-100 dark:bg-rose-900/30',
    border: 'border-rose-200 dark:border-rose-800',
    label: 'Ação Necessária',
  },
  completed: {
    text: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    border: 'border-emerald-200 dark:border-emerald-800',
    label: 'Concluído',
  },
  failed: {
    text: 'text-red-700 dark:text-red-400',
    bg: 'bg-red-100 dark:bg-red-900/30',
    border: 'border-red-200 dark:border-red-800',
    label: 'Negado',
  },
};
